import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { propertylist, propertyInfo, propertyDatasets } from "@services";
import { propertyPayloadType, PropertyState } from "@interface";
import axios from "axios";
import { propertyRoutes } from "@services";


// Generic function to create async thunks for each API call
const createPropertyThunk = (endpoint: string) =>
    createAsyncThunk(`${propertyRoutes.property}/${endpoint}`, async ({ bbl, format }: propertyPayloadType, { rejectWithValue }) => {
        try {
            const response = await propertylist({ bbl, format, endpoint });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    });

export const fetchSalesAndFinancingList = createPropertyThunk("acrisrealmasters");
export const fetchMarshalEvictionsList = createPropertyThunk("evictions");
export const fetchHPDComplaintsAndProblemsList = createPropertyThunk("hpdcomplaints");
export const fetchHPDViolationsList = createPropertyThunk("hpdviolations");
export const fetchDOBComplaintsList = createPropertyThunk("dobcomplaints");
export const fetchDOBViolationsList = createPropertyThunk("dobviolations");
export const fetchECBViolationsList = createPropertyThunk("ecbviolations");
export const fetchDobFiledPermits = createPropertyThunk("dobfiledpermits");
export const fetchDobIssuedPermits = createPropertyThunk("dobissuedpermits");
export const fetchHousingLitigations = createPropertyThunk("housinglitigations");

export const fetchPropertyInfo = createAsyncThunk(
    propertyRoutes.property,
    async (bbl: string, { rejectWithValue }) => {
        try {
            const response = await propertyInfo(bbl);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const fetchPropertyDataset = createAsyncThunk(
    propertyRoutes.dataset,
    async (arg, { rejectWithValue }) => {
        try {
            const response = await propertyDatasets();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState: PropertyState = {
    propertyInfo: {
        data: {
            bbl: "",
            council: "",
            cd: "",
            stateassembly: "",
            statesenate: "",
            zipcode: "",
            unitstotal: "",
            unitsres: "",
            yearbuilt: "",
            ownername: "",
            zonedist1: "",
            overlay1: "",
            spdist1: "",
            builtfar: "",
            residfar: "",
            commfar: "",
            facilfar: "",
            unitsrentstabilized: "",
            hpdregistrations: [],
            rentstabilizationrecord: {}
        }, loading: false, error: null
    },
    propertyDataset: { data: null, loading: false, error: null },
    salesAndFinancing: { data: null, loading: false, error: null },
    marshalEvictions: { data: null, loading: false, error: null },
    hpdComplaints: { data: null, loading: false, error: null },
    hpdViolations: { data: null, loading: false, error: null },
    dobComplaints: { data: null, loading: false, error: null },
    dobViolations: { data: null, loading: false, error: null },
    ecbViolations: { data: null, loading: false, error: null },
    dobFiledPermits: { data: null, loading: false, error: null },
    dobIssuedPermits: { data: null, loading: false, error: null },
    housingLitigations: { data: null, loading: false, error: null }
};
// Create Slice
export const propertySlice = createSlice({
    name: "property",
    initialState,
    reducers: {
        resetPropertyData: (state) => {
            // Reset all properties to their initial state
            Object.keys(state).forEach((key) => {
                state[key as keyof PropertyState] = initialState[key as keyof PropertyState];
            });
        }
    },
    extraReducers: (builder) => {
        // property datasets
        builder
            .addCase(fetchPropertyDataset.pending, (state) => {
                state.propertyDataset.loading = true;
                state.propertyDataset.error = null;
            })
            .addCase(fetchPropertyDataset.fulfilled, (state, action) => {
                state.propertyDataset.loading = false;
                state.propertyDataset.data = action.payload;
            })
            .addCase(fetchPropertyDataset.rejected, (state, action) => {
                state.propertyDataset.loading = false;
                state.propertyDataset.error = action.payload as string;
            });
        // property information
        builder
            .addCase(fetchPropertyInfo.pending, (state) => {
                state.propertyInfo.loading = true;
                state.propertyInfo.error = null;
            })
            .addCase(fetchPropertyInfo.fulfilled, (state, action) => {
                state.propertyInfo.loading = false;
                state.propertyInfo.data = action.payload;
            })
            .addCase(fetchPropertyInfo.rejected, (state, action) => {
                state.propertyInfo.loading = false;
                state.propertyInfo.error = action.payload as string;
            });
        // Sales and Financing
        builder
            .addCase(fetchSalesAndFinancingList.pending, (state) => {
                state.salesAndFinancing.loading = true;
                state.salesAndFinancing.error = null;
            })
            .addCase(fetchSalesAndFinancingList.fulfilled, (state, action) => {
                state.salesAndFinancing.loading = false;
                state.salesAndFinancing.data = action.payload;
            })
            .addCase(fetchSalesAndFinancingList.rejected, (state, action) => {
                state.salesAndFinancing.loading = false;
                state.salesAndFinancing.error = action.payload as string;
            });

        // Marshal Evictions
        builder
            .addCase(fetchMarshalEvictionsList.pending, (state) => {
                state.marshalEvictions.loading = true;
                state.marshalEvictions.error = null;
            })
            .addCase(fetchMarshalEvictionsList.fulfilled, (state, action) => {
                state.marshalEvictions.loading = false;
                state.marshalEvictions.data = action.payload;
            })
            .addCase(fetchMarshalEvictionsList.rejected, (state, action) => {
                state.marshalEvictions.loading = false;
                state.marshalEvictions.error = action.payload as string;
            });

        // HPD complaints
        builder
            .addCase(fetchHPDComplaintsAndProblemsList.pending, (state) => {
                state.hpdComplaints.loading = true;
                state.hpdComplaints.error = null;
            })
            .addCase(fetchHPDComplaintsAndProblemsList.fulfilled, (state, action) => {
                state.hpdComplaints.loading = false;
                state.hpdComplaints.data = action.payload;
            })
            .addCase(fetchHPDComplaintsAndProblemsList.rejected, (state, action) => {
                state.hpdComplaints.loading = false;
                state.hpdComplaints.error = action.payload as string;
            });

        // HPD violations
        builder
            .addCase(fetchHPDViolationsList.pending, (state) => {
                state.hpdViolations.loading = true;
                state.hpdViolations.error = null;
            })
            .addCase(fetchHPDViolationsList.fulfilled, (state, action) => {
                state.hpdViolations.loading = false;
                state.hpdViolations.data = action.payload;
            })
            .addCase(fetchHPDViolationsList.rejected, (state, action) => {
                state.hpdViolations.loading = false;
                state.hpdViolations.error = action.payload as string;
            });

        // DOB complaints
        builder
            .addCase(fetchDOBComplaintsList.pending, (state) => {
                state.dobComplaints.loading = true;
                state.dobComplaints.error = null;
            })
            .addCase(fetchDOBComplaintsList.fulfilled, (state, action) => {
                state.dobComplaints.loading = false;
                state.dobComplaints.data = action.payload;
            })
            .addCase(fetchDOBComplaintsList.rejected, (state, action) => {
                state.dobComplaints.loading = false;
                state.dobComplaints.error = action.payload as string;
            });

        // DOB violations
        builder
            .addCase(fetchDOBViolationsList.pending, (state) => {
                state.dobViolations.loading = true;
                state.dobViolations.error = null;
            })
            .addCase(fetchDOBViolationsList.fulfilled, (state, action) => {
                state.dobViolations.loading = false;
                state.dobViolations.data = action.payload;
            })
            .addCase(fetchDOBViolationsList.rejected, (state, action) => {
                state.dobViolations.loading = false;
                state.dobViolations.error = action.payload as string;
            });

        // ECB violations
        builder
            .addCase(fetchECBViolationsList.pending, (state) => {
                state.ecbViolations.loading = true;
                state.ecbViolations.error = null;
            })
            .addCase(fetchECBViolationsList.fulfilled, (state, action) => {
                state.ecbViolations.loading = false;
                state.ecbViolations.data = action.payload;
            })
            .addCase(fetchECBViolationsList.rejected, (state, action) => {
                state.ecbViolations.loading = false;
                state.ecbViolations.error = action.payload as string;
            });

        // DOB filed permits
        builder
            .addCase(fetchDobFiledPermits.pending, (state) => {
                state.dobFiledPermits.loading = true;
                state.dobFiledPermits.error = null;
            })
            .addCase(fetchDobFiledPermits.fulfilled, (state, action) => {
                state.dobFiledPermits.loading = false;
                state.dobFiledPermits.data = action.payload;
            })
            .addCase(fetchDobFiledPermits.rejected, (state, action) => {
                state.dobFiledPermits.loading = false;
                state.dobFiledPermits.error = action.payload as string;
            });

        // DOB issued permits
        builder
            .addCase(fetchDobIssuedPermits.pending, (state) => {
                state.dobIssuedPermits.loading = true;
                state.dobIssuedPermits.error = null;
            })
            .addCase(fetchDobIssuedPermits.fulfilled, (state, action) => {
                state.dobIssuedPermits.loading = false;
                state.dobIssuedPermits.data = action.payload;
            })
            .addCase(fetchDobIssuedPermits.rejected, (state, action) => {
                state.dobIssuedPermits.loading = false;
                state.dobIssuedPermits.error = action.payload as string;
            });

        // Housing Litigations
        builder
            .addCase(fetchHousingLitigations.pending, (state) => {
                state.housingLitigations.loading = true;
                state.housingLitigations.error = null;
            })
            .addCase(fetchHousingLitigations.fulfilled, (state, action) => {
                state.housingLitigations.loading = false;
                state.housingLitigations.data = action.payload;
            })
            .addCase(fetchHousingLitigations.rejected, (state, action) => {
                state.housingLitigations.loading = false;
                state.housingLitigations.error = action.payload as string;
            });
    },
});

export const { resetPropertyData } = propertySlice.actions;